import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Tooltip
} from '@mui/material';
import { ContentCopy, Close } from '@mui/icons-material';
import EXPORT_SCRIPTS from '../config/exportScripts';

/**
 * "How to export your Intune data" reference. Lives in a dialog so the Offline
 * workspace can stay full-height instead of scrolling past a wall of script.
 */
const ExportScriptsDialog = ({ open, onClose, onCopied, onCopyFailed }) => {
  const copy = (entry) => {
    navigator.clipboard
      .writeText(entry.script)
      .then(() => onCopied && onCopied(`${entry.title} script copied to clipboard!`))
      .catch(() => onCopyFailed && onCopyFailed('Failed to copy to clipboard'));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        How to Export Your Intune Data
        <IconButton size="small" onClick={onClose}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Run one of these scripts against your tenant to produce a JSON file you can upload here. No
          connection to Microsoft Graph is needed from this site in offline mode.
        </Typography>

        {EXPORT_SCRIPTS.map((entry) => (
          <Box key={entry.id} sx={{ mb: 4 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }} gutterBottom>
              {entry.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {entry.description}
            </Typography>

            <Box
              sx={{
                position: 'relative',
                bgcolor: (theme) => (theme.palette.mode === 'dark' ? '#1e1e1e' : '#f5f5f5'),
                border: (theme) => `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                overflow: 'hidden',
                mt: 1,
                '& pre': {
                  margin: 0,
                  padding: '16px',
                  paddingRight: '64px',
                  overflowX: 'auto',
                  overflowY: 'auto',
                  maxHeight: 240,
                  fontFamily: "'Consolas', 'Monaco', 'Courier New', monospace",
                  fontSize: '0.75rem',
                  lineHeight: 1.5,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                  color: (theme) => (theme.palette.mode === 'dark' ? '#d4d4d4' : '#333')
                }
              }}
            >
              <Tooltip title="Copy to clipboard">
                <IconButton
                  size="small"
                  onClick={() => copy(entry)}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    bgcolor: (theme) =>
                      theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                    '&:hover': {
                      bgcolor: (theme) =>
                        theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'
                    }
                  }}
                >
                  <ContentCopy fontSize="small" />
                </IconButton>
              </Tooltip>
              <pre>{entry.script}</pre>
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Writes to <code>{entry.outputPath}</code> - change the output path in the script to suit.
            </Typography>
          </Box>
        ))}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExportScriptsDialog;
